import { SystemDevice, IXResponse as IResponse, ISystemDevice } from '@/types/device.type';
// import { IDevice } from '@/views/management/components/setup/DeviceSetup.vue';
import { CustomResponse, IDevice, IEmployee, IEmployeeRole, SystemError } from '@posx/core';
import secureStorage from '@xpos/utils/src/secure.storage.utils';
import axios from 'axios';
import { defineStore } from 'pinia';
import { SystemDeviceType } from '../types/device.type';
import { createRequest } from '@/utils/http';
import { Capacitor } from '@capacitor/core';
import { getPlatforms, isPlatform } from '@ionic/vue';
import { usePrinterStore } from './printer';
import { useConfigStore } from './config';
import { cashdrawer } from '@posx/imin-cashdrawer';
import { EscPosPrinter, PrinterConnections } from '@posx/core';
import { getDeviceId, getCDSDeviceId } from '@/capacitors/utils';
import { getPlatform } from '@/utils/misc';
import { logInfo, logError } from '@/utils/logger';
import { notify } from '@/utils/notification';
import { PresentationService } from '@/services/presentation.service';
import { i18n } from '@/main';

export const useDevice = defineStore('devices', {
  state: () => ({
    cdsDevice: null as ISystemDevice | null,
    cdsDeviceUid: null as string | null,
    currentDevice: null as IDevice | null,
    platform: null as string | null
  }),
  actions: {
    async getDevices(): Promise<ISystemDevice[]> {
      const merchantUid = secureStorage.getItem('merchant_uid');
      if (!merchantUid) throw new SystemError('Merchant uid is not ready, or not found');
      const instance = axios.create({
        baseURL: import.meta.env.VITE_BASE_URL,
        timeout: 30000,
        headers: {
          Authorization: 'Bearer ' + secureStorage.getAccessToken(),
        },
      });

      const res = await instance.get<IResponse<ISystemDevice[]>>(`/api/v5/merchants/${merchantUid}/devices`);
      if (res.status === 200) {
        return res.data.data;
      } else return [];
    },
    async getCdsDevice(): Promise<ISystemDevice> {
      if (this.cdsDevice) return this.cdsDevice;
      const devices = await this.getDevices();
      const cdsDevices = devices.filter((d) => d.type === SystemDeviceType.CDS &&
        (d.deleted_at === null || d.deleted_at === undefined));
      if (cdsDevices.length === 0) throw new SystemError('There is no Customer Display System connected');
      // Local CDS if settings on + secondary display; else latest calibrated; else caller uses default
      const cds = PresentationService.getInstance();
      if (cds.isEnabled() && cds.isAvailable()) {
        const cdsUid = await this.getCdsUid();
        const found = cdsDevices.find(d => d.uid === cdsUid);
        if (found) { this.cdsDevice = found; return found; }
      }
      const calibrated = cdsDevices.filter(d => d.etc?.cds_screen_ratio_updated_at);
      if (calibrated.length) {
        calibrated.sort((a, b) => new Date(b.etc!.cds_screen_ratio_updated_at!).getTime() - new Date(a.etc!.cds_screen_ratio_updated_at!).getTime());
        this.cdsDevice = calibrated[0]; return calibrated[0];
      }
      throw new SystemError('No calibrated CDS device found');
    },
    // Get CDS device uid: sync_to_specific_cds > cached > register with same hardware_id as CDS URL
    async getCdsUid(): Promise<string | null> {
      const currentDevice = await this.getCurrentDevice();
      const specificCds = currentDevice?.etc?.sync_to_specific_cds;
      if (specificCds === 'disabled') return null;
      if (specificCds) return specificCds;
      if (!PresentationService.getInstance().isEnabled()) return null;
      if (this.cdsDeviceUid) return this.cdsDeviceUid;
      const merchantUid = secureStorage.getItem('merchant_uid');
      const hardwareId = await getCDSDeviceId();
      const result = await createRequest().post(`/api/v5/merchants/${merchantUid}/register-device`, {
        hardware_id: hardwareId,
        is_main: false,
        type: 'cds'
      });
      if (result.data?.error_code === 0)
        this.cdsDeviceUid = result.data.data.uid;
      logInfo('CDS device registered', { hardware_id: hardwareId, uid: this.cdsDeviceUid });
      return this.cdsDeviceUid;
    },
    async getCurrentDevice(forceRefresh = false): Promise<IDevice | null> {
      if (!forceRefresh && this.currentDevice?.uid) return this.currentDevice;
      const merchantUid = secureStorage.getItem('merchant_uid');
      const deviceUid = secureStorage.getItem('device_uid');
      const instance = createRequest();
      const res = await instance.get<CustomResponse<IDevice>>(`/api/v5/merchants/${merchantUid}/devices/${deviceUid}`);
      if (res.data.error) throw new SystemError(`Failed to get device, ${res.data.error}`);
      this.currentDevice = res.data.data;
      return this.currentDevice;
    },
    // Fetch device by uid for cross-device refund
    async getDeviceById(deviceUid: string): Promise<IDevice | null> {
      const merchantUid = secureStorage.getItem('merchant_uid');
      const res = await createRequest().get<CustomResponse<IDevice>>(`/api/v5/merchants/${merchantUid}/devices/${deviceUid}`);
      return res.data.error ? null : res.data.data;
    },
    async openCashDrawer(manual = false) {
        const configStore = useConfigStore();
        // Skip: log for Datadog, toast only on manual Open Drawer (auto flows run on every cash payment/shift), false = not opened
        const skip = (key: string, connection_type?: string) => { logInfo(`Cash Drawer : Skip, ${key}`, { platform: getPlatform(), connection_type }); if (manual) notify.error(i18n.global.t(key, { type: connection_type })); return false; };
        if(configStore.appConfig?.config.cash_drawer.connection_mode === 'via_imin_pos') {
            if (getPlatform() !== 'android') return; // IMIN cash drawer only supported on Android
            await cashdrawer.open();
        } else {
            const printerStore = usePrinterStore();
            const printerService = await printerStore.getService();

            switch (getPlatform()) {
                case 'electron':
                const receiptPrinter = await printerService.getDefaultReceiptPrinter();
                if (!receiptPrinter) return skip('cashDrawerTest.printerNotFound');
                const kickCode = configStore.appConfig?.config.cash_drawer.kick_code || '27, 112, 48, 55, 121';
                const bytes = kickCode.split(',').map(d => parseInt(d.trim(), 10)).filter(b => !isNaN(b) && b >= 0 && b <= 255);
                const byteArray = new Uint8Array(bytes);
                // Drawer kick failure (e.g. Windows printer renamed/removed) must not block payment/shift flows, false = failed
                if (receiptPrinter.connection_type === PrinterConnections.WINDOWS_DRIVER && receiptPrinter.driver_printer_name)
                    return await window.electronPrinter.directPrint(receiptPrinter.driver_printer_name, byteArray).catch((error: Error) => {
                        logError('Open cash drawer failed', { printer_name: receiptPrinter.driver_printer_name, error: error?.message });
                        notify.error(error?.message);
                        return false;
                    });
                if (receiptPrinter.connection_type === PrinterConnections.WINDOWS_DRIVER) return skip('cashDrawerTest.driverNotConfigured');
                // Electron kicks only via Windows driver; WINDOWS_DRIVER_BUILT_IN left as is
                if (receiptPrinter.connection_type !== PrinterConnections.WINDOWS_DRIVER_BUILT_IN) return skip('cashDrawerTest.unsupportedConnection', receiptPrinter.connection_type);
                    break;
                case 'web':
                case 'ios':
                case 'android':
                    if (!await printerService.getDefaultReceiptPrinter()) return skip('cashDrawerTest.printerNotFound'); // No receipt printer configured
                    await printerService.openCashDrawer();
                    break;
                default:
                    throw new SystemError('Cash drawer is not supported on this platform');
            }

        }
    },
    /**
     * Check if merchant has a main device registered
     */
    async hasMainDevice(uid: string): Promise<boolean> {
      const instance = axios.create({
        baseURL: import.meta.env.VITE_BASE_URL,
        timeout: 30000,
        headers: {
          Authorization: 'Bearer ' + secureStorage.getAccessToken(),
        },
      });
      const result = await instance.get(`/api/v5/merchants/${uid}/check-main-device`);
      return !!(result.status === 200 && result.data?.data);
    },

    /**
     * Register device for a merchant
     */
    async registerDevice(uid: string, is_main: boolean): Promise<string | null> {
      const deviceId = await getDeviceId();
      const instance = axios.create({
        baseURL: import.meta.env.VITE_BASE_URL,
        timeout: 30000,
        headers: {
          Authorization: 'Bearer ' + secureStorage.getAccessToken(),
        },
      });
      let deviceType = 'client';
      if(getPlatform() === 'desktop-web') deviceType = 'desktop';
      else if(getPlatform() === 'mobile-web') deviceType = 'mobile';
      // Remote devices (desktop/mobile) have no SignalR - force is_main=false to prevent silent order loss
      if (deviceType !== 'client') is_main = false;
      const result = await instance.post(`/api/v5/merchants/${uid}/register-device`, {
        hardware_id: deviceId,
        is_main,
        type: deviceType
      });

      if (result.status === 200 && result.data.error_code == 0) {
        const deviceUid = result.data.data.uid;
        const isMainDevice = result.data.data.is_main;
        secureStorage.setItem('device_uid', deviceUid);
        secureStorage.setItem('is_main_device', isMainDevice.toString());
        logInfo(`Device: Registered, Device ${deviceUid}`, { merchant_uid: uid, is_main: isMainDevice, type: deviceType, hardware_id: deviceId });
        return deviceUid;
      }
      logError(`Device: Register Failed, Merchant ${uid}`, { is_main, type: deviceType, hardware_id: deviceId, error_code: result.data?.error_code });
      return null;
    }
  },
});
